import { FriendlyCaptchaSDK } from '@friendlycaptcha/sdk';

export default class {
  /**
   * @type {object<string, WidgetHandle>}
   */
  #widgets = {};

  #fieldClass = 'frc-captcha';

  #sdk;

  /**
   * Event handler for submit button click
   *
   * Prevent form submit if captcha is not completed
   *
   * @param {Event} e Submit button click event
   *
   * @return {void}
   */
  #handlerSubmitClick(e) {
    const cf7 = e.target.closest('.wpcf7');
    if (!cf7) {
      return;
    }

    const formId = this.#cf7FormId(cf7);
    if (
      !formId ||
      !this.#widgets[formId] ||
      this.#widgets[formId].getState() === 'completed'
    ) {
      return;
    }

    // prevent submit if captcha is not completed
    e.preventDefault();
    e.stopImmediatePropagation();
  }

  /**
   * Move the field BEFORE the submit button
   *
   * @param {HTMLElement} $field Field to move
   *
   * @return {void}
   */
  #moveField($field) {
    const button = $field.closest('.wpcf7-form').querySelector('.wpcf7-submit');
    if (button) {
      button.parentNode.insertBefore($field, button);
    }
  }

  /**
   * Get the unique CF7 instance ID (page and modal can share a form ID)
   *
   * @param {HTMLElement} cf7 Contact Form 7 element with a unique `id` attribute
   *
   * @return {string}
   */
  #cf7FormId(cf7) {
    return cf7.id ?? '';
  }

  /**
   * Get form ID from CF7 event
   *
   * @param {Event} e CF7 event
   *
   * @return {string}
   */
  #eventFormId(e) {
    return e.detail?.unitTag ?? '';
  }

  /**
   * Initialize service
   *
   * @return {void}
   */
  init() {
    // init properties
    this.#sdk = new FriendlyCaptchaSDK();

    // creater widgets
    document
      .querySelectorAll(`.wpcf7[id]`)
      .forEach((cf7) => this.createWidget(this.#cf7FormId(cf7)));

    // reset widgets after mail sent
    document.addEventListener('wpcf7mailsent', (e) =>
      this.resetWidget(this.#eventFormId(e))
    );
  }

  /**
   * Create captcha widget
   *
   * @param {string} formId CF7 form ID
   *
   * @return {void}
   */
  createWidget(formId) {
    this.destroyWidget(formId);

    const component = document.getElementById(formId);
    if (!component) {
      return;
    }

    const field = component.querySelector(`.${this.#fieldClass}`);
    if (!field || !(field.dataset?.sitekey ?? '')) {
      return;
    }

    // move the field if automove is enabled
    if ((field.dataset?.automove ?? '0') === '1') {
      this.#moveField(field);
    }

    this.#widgets[formId] = this.#sdk.createWidget({
      element: field,
      sitekey: field.dataset.sitekey,
      startMode: 'focus',
      theme: field.dataset?.theme ?? 'auto',
      apiEndpoint: field.dataset?.apiEndpoint ?? 'eu',
      formFieldName: field.dataset?.formFieldName ?? 'frc-captcha-response'
    });

    component
      .querySelector('.wpcf7-submit')
      .addEventListener('click', (e) => this.#handlerSubmitClick(e));
  }

  /**
   * Reset captcha widget
   *
   * @param {string} formId CF7 form ID
   *
   * @return {void}
   */
  resetWidget(formId) {
    if (!formId || !this.#widgets[formId]) {
      return;
    }

    this.#widgets[formId].reset();
  }

  /**
   * Reset all widgets
   *
   * @return {void}
   */
  reset() {
    for (const id in this.#widgets) {
      this.resetWidget(id);
    }
  }

  /**
   * Destroy captcha widget
   *
   * @param {string} formId CF7 form ID
   *
   * @return {void}
   */
  destroyWidget(formId) {
    if (!formId || !this.#widgets[formId]) {
      return;
    }

    this.#widgets[formId].destroy();
    delete this.#widgets[formId];
  }

  /**
   * Destroy all widgets
   *
   * @return {void}
   */
  destroy() {
    for (const id in this.#widgets) {
      this.destroyWidget(id);
    }
  }
}
